import { fmtNum } from '../ui/ListFoot';
import type { Status } from '../book/types';
import { statusName } from '../book/model';
import { titleKey } from '../queue/model';

// Discover › Awards & lists and a list's page (Phase 7 Part 3a, boards 11, 14, 12 and 12p; agreed
// with Kristi Nov 3): browsing only. Lists you follow, the catalog of lists that have books
// loaded, Follow and Hide; a list's page by year, with which of its books are on your list.
// Adding books from a list (Add ▾, Not for me, Select several) is Part 3b.

export type ListKind = 'award' | 'best_of_year' | 'bestseller' | 'one_time' | 'book_club';

export const KIND_NAMES: Record<ListKind, [string, string]> = {
  award: ['Award', 'Awards'],
  best_of_year: ['Best of the year', 'Best of the year'],
  bestseller: ['Bestseller', 'Bestsellers'],
  one_time: ['One-time list', 'One-time lists'],
  book_club: ['Book club', 'Book clubs'],
};
export const KIND_ORDER: ListKind[] = ['award', 'best_of_year', 'bestseller', 'one_time', 'book_club'];

/** A level's name ("winner" → "Winner", "longlist" → "Longlist"). */
export const levelName = (l: string | null | undefined) => (l ? l.charAt(0).toUpperCase() + l.slice(1).replace(/_/g, ' ') : '');

export interface CatalogList {
  id: string;
  name: string;
  kind: ListKind;
  publisher: string | null;
  frequency: string | null;
  since: number | null;
  levels: string[];
  source: string | null;
  /** The years loaded, newest first. */
  years: number[];
  /** Distinct books on this list (any year) that are on your list. */
  onYourList: number;
  following: boolean;
  hidden: boolean;
}

/** "Levels: Winner · Shortlist · Longlist". */
export const levelsLine = (l: Pick<CatalogList, 'levels'>) => (l.levels.length ? `Levels: ${l.levels.map(levelName).join(' · ')}` : '');

/** "Best of the year · yearly". */
export const kindLine = (l: Pick<CatalogList, 'kind' | 'frequency'>) => [KIND_NAMES[l.kind][0], l.frequency].filter(Boolean).join(' · ');

export const booksText = (n: number) => (n ? `${fmtNum(n)} ${n === 1 ? 'book' : 'books'}` : '—');

export type CatalogSortKey = 'name' | 'kind' | 'frequency' | 'since' | 'mine';
export interface CatalogSort {
  key: CatalogSortKey;
  desc?: boolean;
}

/** The catalog table's rows: kind order, then name, unless a header says otherwise. */
export function sortCatalog(lists: CatalogList[], sort: CatalogSort): CatalogList[] {
  const byName = (a: CatalogList, b: CatalogList) => titleKey(a.name).localeCompare(titleKey(b.name));
  const cmp: Record<CatalogSortKey, (a: CatalogList, b: CatalogList) => number> = {
    name: byName,
    kind: (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || byName(a, b),
    frequency: (a, b) => (a.frequency ?? '￿').localeCompare(b.frequency ?? '￿') || byName(a, b),
    since: (a, b) => (a.since ?? 99999) - (b.since ?? 99999) || byName(a, b),
    mine: (a, b) => a.onYourList - b.onYourList || byName(a, b),
  };
  const out = [...lists].sort(cmp[sort.key]);
  return sort.desc ? out.reverse() : out;
}

/** How many lists of each kind (the kind chips' counts), among the lists shown. */
export function kindCounts(lists: CatalogList[]): Partial<Record<ListKind, number>> {
  const out: Partial<Record<ListKind, number>> = {};
  for (const l of lists) out[l.kind] = (out[l.kind] ?? 0) + 1;
  return out;
}

// ---------- a list's page ----------

export interface ListEntry {
  id: string;
  rank: number | null;
  level: string | null;
  category: string | null;
  title: string;
  author: string;
  workId: string | null;
  literary: string | null;
  cover: string | null;
  /** Your book of this work, when it's on your list. */
  mine: { id: string; label: number; status: Status | null } | null;
}

export type EntrySortKey = 'order' | 'title' | 'author' | 'level' | 'category' | 'literary' | 'mine';
export interface EntrySort {
  key: EntrySortKey;
  desc?: boolean;
}

/** The list's own order: by rank when it has ranks, else by level (winner first), then title. */
export function ownOrder(entries: ListEntry[], levels: string[]): ListEntry[] {
  const lv = (e: ListEntry) => {
    const i = levels.indexOf(e.level ?? '');
    return i < 0 ? levels.length : i;
  };
  return [...entries].sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9) || lv(a) - lv(b) || titleKey(a.title).localeCompare(titleKey(b.title)));
}

const SHELF_ORDER = ['currently_reading', 'up_next', 'future_reads', 'finished', 'not_for_me', 'reference'];

export function sortEntries(entries: ListEntry[], levels: string[], sort: EntrySort): ListEntry[] {
  const base = ownOrder(entries, levels);
  if (sort.key === 'order') return sort.desc ? base.reverse() : base;
  const pos = new Map(base.map((e, i) => [e.id, i]));
  const tie = (a: ListEntry, b: ListEntry) => pos.get(a.id)! - pos.get(b.id)!;
  const text = (s: string | null) => (s ? s.toLocaleLowerCase('en-US') : '￿');
  const mine = (e: ListEntry) => (e.mine ? (SHELF_ORDER.indexOf(e.mine.status ?? '') + 1 || SHELF_ORDER.length + 1) : 99);
  const cmp: Record<Exclude<EntrySortKey, 'order'>, (a: ListEntry, b: ListEntry) => number> = {
    title: (a, b) => titleKey(a.title).localeCompare(titleKey(b.title)),
    author: (a, b) => text(surnameOf(a.author)).localeCompare(text(surnameOf(b.author))),
    level: (a, b) => {
      const i = (e: ListEntry) => (levels.indexOf(e.level ?? '') + 1 || levels.length + 1);
      return i(a) - i(b);
    },
    category: (a, b) => text(a.category).localeCompare(text(b.category)),
    literary: (a, b) => text(a.literary).localeCompare(text(b.literary)),
    mine: (a, b) => mine(a) - mine(b),
  };
  const by = cmp[sort.key as Exclude<EntrySortKey, 'order'>];
  const out = base.sort((a, b) => by(a, b) || tie(a, b));
  return sort.desc ? out.reverse() : out;
}

/** The last word of the first name ("Jo Nesbø; Neil Smith" → "Nesbø"), for sorting by author. */
export const surnameOf = (author: string) => (author.split(/;| and |, /)[0] ?? '').trim().split(/\s+/).at(-1) ?? '';

/** The Your list column: the shelf's name ("Future reads"), empty when it isn't on your list. */
export const mineText = (e: Pick<ListEntry, 'mine'>) => (e.mine ? statusName(e.mine.status) : '');

/** "8 on your list · 59 not on it". */
export function youLine(entries: ListEntry[]): string {
  const on = entries.filter((e) => e.mine).length;
  return `${fmtNum(on)} on your list · ${fmtNum(entries.length - on)} not on it`;
}

/** Which columns a year's list needs: Level when it has more than one, Category when any has one, # when ranked. */
export function columnsFor(entries: ListEntry[]): { rank: boolean; level: boolean; category: boolean } {
  return {
    rank: entries.some((e) => e.rank !== null),
    level: new Set(entries.map((e) => e.level ?? '')).size > 1,
    category: entries.some((e) => e.category),
  };
}

export const LITERARY_NAMES: Record<string, string> = { fiction: 'Fiction', nonfiction: 'Nonfiction', poetry: 'Poetry', drama: 'Drama' };

/** One of a book's list entries, for its book page's Awards & lists row (n127, Kristi Nov 3). */
export interface Honour {
  listId: string;
  list: string;
  year: number;
  label: string | null;
  level: string | null;
  category: string | null;
  rank: number | null;
  sortOrder: number;
}

// Levels that say nothing a list's name doesn't (every Oprah book is a pick).
const QUIET_LEVELS = new Set(['pick', 'selected', 'year_end']);

/** "Booker Prize 2019 · Shortlist", "NPR Books We Love 2025 (Fiction)", "The 100 Best Books… 2024 · #12". */
export function honourLine(h: Honour): string {
  const head = `${h.list} ${h.year}${h.label ? ` (${h.label})` : ''}`;
  const lvl = h.level === 'ranked' ? (h.rank !== null ? `#${h.rank}` : null) : h.level === 'reached_1' ? 'Reached #1' : QUIET_LEVELS.has(h.level ?? '') ? null : levelName(h.level) || null;
  return [head, lvl, h.category?.trim() || null].filter(Boolean).join(' · ');
}

/** Newest year first; within a year, the lists in their catalog order. */
export const sortHonours = (hs: Honour[]) =>
  [...hs].sort((a, b) => b.year - a.year || a.sortOrder - b.sortOrder || a.list.localeCompare(b.list) || (a.rank ?? 0) - (b.rank ?? 0));
